import React, { useEffect, useState } from "react";
import { Plus, Edit3, Trash2, X, Phone, Mail, Award, MessageSquare, KeyRound, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmModal";
import { useAuth } from "@/context/AuthContext";
import RequestAvailabilityModal from "@/components/RequestAvailabilityModal";
import HelpHint from "@/components/HelpHint";
import SortHeader, { useSort } from "@/components/SortHeader";

function OfficialModal({ existing, grades, onSave, onClose }) {
  const [f, setF] = useState({
    first_name: existing?.first_name || "",
    last_name: existing?.last_name || "",
    phone: existing?.phone || "",
    email: existing?.email || "",
    referee_grade_id: existing?.referee_grade_id || "",
    scorer_grade_id: existing?.scorer_grade_id || "",
    role_referee: Array.isArray(existing?.roles) ? existing.roles.includes("referee") : true,
    role_scorer: Array.isArray(existing?.roles) ? existing.roles.includes("scorer") : false,
  });
  const refGrades = grades.filter((g) => g.type === "referee");
  const scGrades = grades.filter((g) => g.type === "scorer");

  const submit = () => {
    if (!f.first_name || !f.last_name) {
      toast.error("Prénom et nom requis");
      return;
    }
    if (!f.role_referee && !f.role_scorer) {
      toast.error("Choisis au moins un rôle (arbitre, marqueur ou les deux)");
      return;
    }
    const roles = [];
    if (f.role_referee) roles.push("referee");
    if (f.role_scorer) roles.push("scorer");
    onSave({
      first_name: f.first_name,
      last_name: f.last_name,
      phone: f.phone,
      email: f.email,
      referee_grade_id: f.referee_grade_id || null,
      scorer_grade_id: f.scorer_grade_id || null,
      roles,
    });
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-2xl w-full shadow-2xl my-8" data-testid="official-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="label">{existing ? "Modifier officiel" : "Nouvel officiel"}</div>
            <h3 className="font-serif text-3xl">{existing ? `${existing.first_name} ${existing.last_name}` : "Ajouter à la banque"}</h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-black"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Prénom *">
            <input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="input-box" data-testid="official-firstname-input" />
          </Field>
          <Field label="Nom *">
            <input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="input-box" data-testid="official-lastname-input" />
          </Field>
          <Field label="Téléphone">
            <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input-box" />
          </Field>
          <Field label="Courriel">
            <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-box" />
          </Field>
          <Field label="Grade arbitre">
            <select value={f.referee_grade_id} onChange={(e) => setF({ ...f, referee_grade_id: e.target.value })} className="input-box" data-testid="official-ref-grade-select">
              <option value="">— Aucun —</option>
              {refGrades.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} — {g.rate.toFixed(2)} $
                </option>
              ))}
            </select>
          </Field>
          <Field label="Grade marqueur">
            <select value={f.scorer_grade_id} onChange={(e) => setF({ ...f, scorer_grade_id: e.target.value })} className="input-box" data-testid="official-sc-grade-select">
              <option value="">— Aucun —</option>
              {scGrades.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name} — {g.rate.toFixed(2)} $
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-4">
          <div className="label mb-2">Rôle(s) *</div>
          <div className="grid grid-cols-2 gap-2">
            <label className={`p-2.5 border-2 cursor-pointer transition-colors flex items-center gap-2 ${f.role_referee ? "border-black bg-black/5" : "border-[var(--border-strong)]"}`}>
              <input
                type="checkbox"
                checked={f.role_referee}
                onChange={(e) => setF({ ...f, role_referee: e.target.checked })}
                className="w-4 h-4 border-2 border-black"
                data-testid="official-role-referee"
              />
              <span className="text-sm font-bold">Arbitre</span>
            </label>
            <label className={`p-2.5 border-2 cursor-pointer transition-colors flex items-center gap-2 ${f.role_scorer ? "border-black bg-black/5" : "border-[var(--border-strong)]"}`}>
              <input
                type="checkbox"
                checked={f.role_scorer}
                onChange={(e) => setF({ ...f, role_scorer: e.target.checked })}
                className="w-4 h-4 border-2 border-black"
                data-testid="official-role-scorer"
              />
              <span className="text-sm font-bold">Marqueur</span>
            </label>
          </div>
        </div>
        <p className="text-xs text-[var(--muted)] mt-3 border-l-4 border-black pl-3 italic">
          Un officiel peut être qualifié arbitre, marqueur, ou les deux. Le rôle contrôle dans quel dropdown il apparaît.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={submit} className="btn-primary" data-testid="official-save-btn">Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

export default function OfficialsPage() {
  const { user, refresh: refreshAuth } = useAuth();
  const confirm = useConfirm();
  const [officials, setOfficials] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRequest, setShowRequest] = useState(false);

  const fetchAll = async () => {
    try {
      const [o, g] = await Promise.all([api.get("/officials"), api.get("/grades")]);
      setOfficials(o.data.officials);
      setGrades(g.data.grades);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const create = async (form) => {
    try {
      const { data } = await api.post("/officials", form);
      setOfficials([...officials, data.official]);
      setShowModal(false);
      toast.success("Officiel ajouté");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const update = async (form) => {
    try {
      const { data } = await api.put(`/officials/${editing.id}`, form);
      setOfficials(officials.map((o) => (o.id === editing.id ? data.official : o)));
      setEditing(null);
      toast.success("Officiel mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (o) => {
    const ok = await confirm.ask({
      title: "Supprimer cet officiel?",
      message: `${o.first_name} ${o.last_name} sera retiré de la banque.`,
      confirmLabel: "Supprimer",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/officials/${o.id}`);
      setOfficials(officials.filter((x) => x.id !== o.id));
      toast.success("Officiel supprimé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendResetSms = async (o) => {
    const ok = await confirm.ask({
      title: "Envoyer un lien de réinitialisation par SMS?",
      message: `Un SMS sera envoyé à ${o.first_name} ${o.last_name} (${o.phone}) avec un lien valide 15 minutes pour réinitialiser son mot de passe.`,
      confirmLabel: "Envoyer",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/officials/${o.id}/send-password-reset`);
      if (data.sms_status === "sent") toast.success("SMS envoyé");
      else if (data.sms_status === "mocked") toast.success("SMS simulé (mode mock — Twilio non configuré)");
      else if (data.sms_status === "failed") toast.error("Échec de l'envoi du SMS");
      else toast.success("Lien généré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const signupUrl = user?.signup_token ? `${window.location.origin}/inscription/${user.signup_token}` : "";
  const copySignup = () => {
    navigator.clipboard.writeText(signupUrl);
    toast.success("Lien d'inscription copié");
  };
  const rotateSignup = async () => {
    const ok = await confirm.ask({
      title: "Régénérer le lien d'inscription ?",
      message: "L'ancien lien deviendra invalide immédiatement. Toute personne qui l'avait deviendra incapable de l'utiliser.",
      confirmLabel: "Régénérer",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.post("/me/signup-token/rotate");
      await refreshAuth();
      toast.success("Nouveau lien généré. L'ancien est révoqué.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pending = officials.filter((o) => o.status === "pending");
  // Filtre par rôle (référees / marqueurs / tous)
  const [roleFilter, setRoleFilter] = useState("all"); // "all" | "referee" | "scorer"
  // Recherche par nom (P1)
  const [query, setQuery] = useState("");
  const normalizedQuery = query
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const matchesQuery = (o) => {
    if (!normalizedQuery) return true;
    const haystack = `${o.first_name || ""} ${o.last_name || ""} ${o.email || ""}`
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return haystack.includes(normalizedQuery);
  };
  const filteredByRole = officials.filter((o) => {
    if (!matchesQuery(o)) return false;
    if (roleFilter === "all") return true;
    return Array.isArray(o.roles) && o.roles.includes(roleFilter);
  });
  const byFirstName = (a, b) => {
    const fa = (a.first_name || "").trim();
    const fb = (b.first_name || "").trim();
    const cmp = fa.localeCompare(fb, "fr", { sensitivity: "base" });
    if (cmp !== 0) return cmp;
    return (a.last_name || "").localeCompare(b.last_name || "", "fr", { sensitivity: "base" });
  };
  const { sortKey, sortDir, cycleSort } = useSort(null);
  const sorted = [...filteredByRole].sort((a, b) => {
    if (!sortKey) return byFirstName(a, b);
    let va, vb;
    switch (sortKey) {
      case "name":
        va = `${a.first_name} ${a.last_name}`.toLowerCase();
        vb = `${b.first_name} ${b.last_name}`.toLowerCase();
        break;
      case "refGrade":
        va = (grades.find((g) => g.id === a.referee_grade_id)?.name || "~").toLowerCase();
        vb = (grades.find((g) => g.id === b.referee_grade_id)?.name || "~").toLowerCase();
        break;
      case "scGrade":
        va = (grades.find((g) => g.id === a.scorer_grade_id)?.name || "~").toLowerCase();
        vb = (grades.find((g) => g.id === b.scorer_grade_id)?.name || "~").toLowerCase();
        break;
      case "contact":
        va = (a.email || a.phone || "~").toLowerCase();
        vb = (b.email || b.phone || "~").toLowerCase();
        break;
      case "source":
        va = a.source || "";
        vb = b.source || "";
        break;
      case "status":
        va = a.status || "";
        vb = b.status || "";
        break;
      default: return 0;
    }
    const cmp = va.localeCompare(vb, "fr", { sensitivity: "base" });
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <div data-testid="officials-page">
      {confirm.modal}
      {showModal && <OfficialModal grades={grades} onSave={create} onClose={() => setShowModal(false)} />}
      {editing && <OfficialModal existing={editing} grades={grades} onSave={update} onClose={() => setEditing(null)} />}
      {showRequest && (
        <RequestAvailabilityModal
          officials={officials}
          onClose={() => setShowRequest(false)}
          onSent={() => {}}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="section-number">02 / Officiels</div>
          <h1 className="h1-big">Ta banque.</h1>
          <p className="text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
            Gère tes arbitres et marqueurs. Valide les inscriptions reçues via formulaire et assigne-leur un grade.
          </p>
        </div>
        <span className="inline-flex items-center">
          <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="new-official-btn">
            <Plus size={14} strokeWidth={2.5} /> Ajouter
          </button>
          <HelpHint
            title="Ajouter un officiel"
            text="Ouvre un formulaire pour ajouter manuellement un arbitre ou un marqueur à ta banque (prénom, nom, téléphone, email, grades). Sans compte associé : il apparaîtra dans le picker mais ne recevra pas de SMS tant qu'il n'a pas créé son compte."
            testid="hint-add-official"
          />
        </span>
      </div>

      <div className="mb-6 flex flex-wrap gap-2 items-center">
        <span className="inline-flex items-center">
          <button onClick={() => setShowRequest(true)} className="btn-secondary" data-testid="request-availability-btn" disabled={officials.length === 0}>
            <MessageSquare size={14} strokeWidth={2.5} /> Demander dispos par SMS
          </button>
          <HelpHint
            title="Demander dispos par SMS"
            text="Ouvre un modal pour envoyer une demande de disponibilités (SMS Twilio) à plusieurs officiels d'un coup pour un événement donné. Tu peux personnaliser le message et choisir les dates."
            testid="hint-request-avail-officials"
          />
        </span>
      </div>

      {signupUrl && (
        <div className="card-flat mb-8" data-testid="signup-link-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="min-w-0">
              <div className="label mb-1">Lien d'inscription officiels</div>
              <div className="font-mono text-xs text-[var(--text)] break-all" data-testid="signup-link-url">{signupUrl}</div>
              <p className="font-mono text-[10px] text-[var(--muted)] mt-2 leading-relaxed">
                Partage ce lien avec tes officiels pour qu'ils créent eux-mêmes leur compte. Tu valideras ensuite leur grade dans la liste ci-dessous.
              </p>
            </div>
            <span className="inline-flex items-center shrink-0 gap-2">
              <button onClick={copySignup} className="btn-secondary" data-testid="copy-signup-btn">
                Copier le lien
              </button>
              <button onClick={rotateSignup} className="btn-secondary" data-testid="rotate-signup-btn">
                Régénérer le lien
              </button>
              <HelpHint
                title="Lien d'inscription public"
                text="Ce lien est unique à ton compte, valide 90 jours. Envoie-le à tes officiels (texto, courriel, QR code). « Régénérer » crée un nouveau lien et invalide l'ancien immédiatement."
                testid="hint-copy-signup"
              />
            </span>
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div className="border-double-thick bg-conflict-stripes-light p-4 mb-6 flex items-start gap-3" data-testid="pending-banner">
          <div className="shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold tabular">{pending.length}</div>
          <div>
            <div className="font-bold text-sm uppercase tracking-wider">À valider</div>
            <div className="text-xs text-[var(--text)]">{pending.length} inscription{pending.length > 1 ? "s" : ""} en attente d'attribution de grade.</div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p>
      ) : officials.length === 0 ? (
        <div className="border-2 border-dashed border-[var(--border-strong)] p-16 text-center">
          <p className="font-serif text-3xl italic mb-2">Banque vide.</p>
          <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)]">
            Ajoute des officiels manuellement ou partage ton lien public.
          </p>
        </div>
      ) : (
        <>
          {/* Filtre par rôle */}
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="role-filter-bar">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Filtre rôle :</span>
            {[
              { v: "all", label: `Tous (${officials.length})` },
              { v: "referee", label: `Arbitres (${officials.filter((o) => Array.isArray(o.roles) && o.roles.includes("referee")).length})` },
              { v: "scorer", label: `Marqueurs (${officials.filter((o) => Array.isArray(o.roles) && o.roles.includes("scorer")).length})` },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setRoleFilter(opt.v)}
                className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${
                  roleFilter === opt.v
                    ? "border-black bg-black text-white"
                    : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black hover:text-black"
                }`}
                data-testid={`role-filter-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
            <div className="relative ml-auto flex items-center" data-testid="officials-search-wrapper">
              <Search size={12} strokeWidth={2.4} className="absolute left-2 text-[var(--muted)] pointer-events-none" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher par nom…"
                className="font-mono text-[11px] border border-[var(--border-strong)] bg-white pl-7 pr-7 py-1 w-48 focus:outline-none focus:border-black"
                data-testid="officials-search-input"
                aria-label="Rechercher un officiel par nom"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-1.5 text-[var(--muted)] hover:text-black"
                  aria-label="Effacer la recherche"
                  data-testid="officials-search-clear"
                >
                  <X size={12} strokeWidth={2.4} />
                </button>
              )}
            </div>
          </div>
        <div className="border-t-2 border-b-2 border-black bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="officials-table">
            <thead>
              <tr className="border-b-2 border-black">
                <SortHeader k="name" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Officiel</SortHeader>
                <SortHeader k="refGrade" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Grade arbitre</SortHeader>
                <SortHeader k="scGrade" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Grade marqueur</SortHeader>
                <SortHeader k="contact" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Contact</SortHeader>
                <SortHeader k="source" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">Source</SortHeader>
                <SortHeader k="status" sortKey={sortKey} sortDir={sortDir} onSort={cycleSort} className="!py-3 !px-3">État</SortHeader>
                <th className="py-3 px-3" />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && normalizedQuery && (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-[var(--muted)] text-xs" data-testid="officials-no-results">
                    Aucun officiel ne correspond à «&nbsp;{query}&nbsp;».{" "}
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="underline hover:text-black"
                    >
                      Réinitialiser la recherche
                    </button>
                  </td>
                </tr>
              )}
              {sorted.map((o) => {
                const refG = grades.find((g) => g.id === o.referee_grade_id);
                const scG = grades.find((g) => g.id === o.scorer_grade_id);
                return (
                  <tr key={o.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-3)]" data-testid={`official-row-${o.id}`}>
                    <td className="py-2.5 px-3 font-mono text-xs">
                      <span className="font-bold">{o.first_name} {o.last_name}</span>
                    </td>
                    <td className="py-2.5 px-3 font-mono text-xs">
                      {refG ? <span className="inline-flex items-center gap-1"><Award size={11} strokeWidth={2.5} />{refG.name}</span> : <span className="text-[var(--muted-2)]">—</span>}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-xs">
                      {scG ? <span className="inline-flex items-center gap-1"><Award size={11} strokeWidth={2.5} />{scG.name}</span> : <span className="text-[var(--muted-2)]">—</span>}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-[10px] text-[var(--muted)]">
                      {o.email && <div className="inline-flex items-center gap-1"><Mail size={10} strokeWidth={2.5} />{o.email}</div>}
                      {o.phone && <div className="inline-flex items-center gap-1"><Phone size={10} strokeWidth={2.5} />{o.phone}</div>}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                      {o.source === "form" ? "Formulaire" : "Manuel"}
                    </td>
                    <td className="py-2.5 px-3">
                      {o.status === "pending" ? (
                        <span className="badge-dashed">À valider</span>
                      ) : (
                        <span className="badge-dark">Validé</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      {o.has_account && o.phone && (
                        <button
                          onClick={() => sendResetSms(o)}
                          className="text-[var(--muted)] hover:text-black mr-2"
                          title="Envoyer un lien de réinitialisation par SMS"
                          data-testid={`reset-password-${o.id}`}
                        >
                          <KeyRound size={13} strokeWidth={2.5} />
                        </button>
                      )}
                      <button onClick={() => setEditing(o)} className="text-[var(--muted)] hover:text-black mr-2" data-testid={`edit-official-${o.id}`}>
                        <Edit3 size={13} strokeWidth={2.5} />
                      </button>
                      <button onClick={() => remove(o)} className="text-[var(--muted)] hover:text-black" data-testid={`delete-official-${o.id}`}>
                        <Trash2 size={13} strokeWidth={2.5} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
